import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "HubSpot プロパティ設定",
  updatedAt: "2026-07-23",
};

export function Content() {
  return (
    <>
      <p>
        書類のステータスや URL を HubSpot レコードに書き込むため、
        どの HubSpot プロパティに何を格納するかを設定します。
        この設定はオブジェクトごとに行います（取引・会社・コンタクト・チケット、
        および HubSpot に登録済みのカスタムオブジェクト）。
      </p>

      <h2>連携オブジェクトの選択</h2>
      <p>
        ダッシュボードの「<strong>プロパティ</strong>」タブを開くと、
        「連携オブジェクト」セクションに、連携可能なオブジェクトが一覧表示されます。
      </p>
      <ul>
        <li><strong>取引（Deals）</strong></li>
        <li><strong>会社（Companies）</strong></li>
        <li><strong>コンタクト（Contacts）</strong></li>
        <li><strong>チケット（Tickets）</strong></li>
        <li><strong>カスタムオブジェクト</strong>（HubSpot に登録があれば自動で一覧に表示・Enterprise 限定）</li>
      </ul>
      <p>
        各オブジェクトの右側にある「<strong>設定する</strong>」（未設定の場合）または
        「<strong>編集</strong>」（設定済みの場合）ボタンを押すと、そのオブジェクト用の
        マッピング設定画面に遷移します。
      </p>
      <ScreenshotBox src="/guide/screenshots/property-setup-1.png" caption="連携オブジェクトの選択画面" />

      <Callout type="tip" title="どのオブジェクトを設定する？">
        多くの場合、<strong>取引（Deals）</strong>のみの設定で十分です。
        会社・コンタクトレコードからも送信したい場合のみ、それぞれ追加で設定してください。
      </Callout>

      <h2>マッピング設定画面の構成</h2>
      <p>
        「設定する」を押すと、以下の項目を含むマッピング画面が開きます。
        画面下部の「<strong>マッピングを保存</strong>」ボタンを押さない限り変更は
        反映されないので、編集後は必ず保存してください。
      </p>
      <ScreenshotBox src="/guide/screenshots/property-setup-2.png" caption="マッピング設定画面全体" />

      <h3>1. プロパティの一括作成</h3>
      <p>
        画面上部にある「<strong>取引（Deals）に全プロパティを一括作成</strong>」ボタンで、
        Sign Extension が必要とするプロパティをまとめて HubSpot 側に作成できます。
        詳しくは <a href="/guide/bulk-properties">プロパティの一括作成</a> を参照してください。
      </p>

      <h3>2. 基本マッピング（必須）</h3>
      <p>
        書類の状態を HubSpot に同期するため、以下 4 つのプロパティを必ず指定します。
        各項目には HubSpot 側のプロパティ名が表示され、ドロップダウンから変更できます。
      </p>
      <ul>
        <li><strong>クラウドサインステータス</strong>: 書類のステータス（下書き / 送信済み / 締結 等）を同期する選択型プロパティ</li>
        <li><strong>クラウドサイン書類 ID</strong>: 書類の内部識別子（テキスト型）</li>
        <li><strong>下書き URL</strong>: 送信前の確認用 URL（テキスト型）</li>
        <li><strong>締結完了 URL</strong>: 署名済 PDF へのリンク URL（テキスト型）</li>
      </ul>

      <h3>3. 送信元ファイルプロパティ</h3>
      <p>
        送信モーダルで選べるようにする契約書 PDF のファイルプロパティを指定します。
        例: <code>contract_file</code>（契約書）、<code>appendix_file</code>（別紙・付属書類）。
        「<strong>+ プロパティを追加</strong>」で複数登録できます。
      </p>
      <p>
        ここに登録したプロパティにファイルが入っていると、送信モーダルの
        「HubSpot プロパティから追加」に候補として並びます。
        送信対象にするには各行の「<strong>+ 追加</strong>」を押してください
        （自動では送信ファイルに入りません）。
      </p>

      <h3>4. 宛先（署名者）設定</h3>
      <p>
        モーダルで宛先（署名者）を埋める方法を 3 つから選びます:
      </p>
      <ul>
        <li><strong>プロパティから取得</strong>: HubSpot の「送付先氏名 1」「送付先メール 1」等のプロパティから自動セット</li>
        <li><strong>関連コンタクトから選択</strong>: HubSpot 取引に紐づくコンタクト一覧から都度選択</li>
        <li><strong>毎回手動入力</strong>: モーダルで毎回入力</li>
      </ul>

      <h3>5. 固定の送付宛先（任意）</h3>
      <p>
        毎回必ず宛先に含める社内承認者などを登録できます。
        例: 法務担当者を必ず承認者として含める運用に。
        詳しくは <a href="/guide/recipients">固定送付宛先・共有先（CC）</a> を参照。
      </p>

      <h3>6. 共有先（CC）設定</h3>
      <p>
        署名権限はないが書類の進捗を共有したい相手（CC 相当）の設定。
        「プロパティから取得」または「毎回手動入力」を選べます。
      </p>

      <h3>7. 締結完了ファイルの処理方式</h3>
      <ul>
        <li><strong>URL のみ保存</strong>: 締結完了 URL を該当プロパティに書き込み（既定）</li>
        <li><strong>HubSpot へ再アップロード</strong>: 締結済 PDF をクラウドサインから取得し、HubSpot のファイルプロパティへ自動アップロード</li>
      </ul>

      <h3>8. 合意締結証明書の処理方式</h3>
      <p>
        クラウドサインが発行する合意締結証明書（PDF）について、扱い方を 3 つから選びます:
      </p>
      <ul>
        <li><strong>格納しない</strong>: 何もしない（必要時はダッシュボードの送信ログから手動 DL 可能）</li>
        <li><strong>URL のみ保存</strong>: 証明書の DL URL をテキストプロパティに格納</li>
        <li><strong>HubSpot へ再アップロード</strong>: 証明書 PDF を HubSpot ファイルへ自動アップロード</li>
      </ul>

      <h3>9. クラウドサインテンプレート</h3>
      <p>
        既定で利用するクラウドサインのテンプレートを指定できます。指定すると、送信モーダルを
        開いた時点で「クラウドサインのテンプレートを利用する」が初期 ON になり、そのテンプレートが
        自動選択されます。送信モーダル側で都度切り替えることも可能です。
      </p>
      <ol>
        <li>「<strong>テンプレート一覧を取得</strong>」ボタンを押して、クラウドサインから
          テンプレート一覧を読み込みます</li>
        <li>ドロップダウンから既定のテンプレートを選択</li>
        <li>「マッピングを保存」で確定</li>
      </ol>
      <p>
        既定を設定しない（「-- 既定なし --」を選択する）と、送信モーダルで毎回テンプレートを
        選ぶ運用になります。</p>

      <h2>保存と反映</h2>
      <p>
        画面下部の「<strong>マッピングを保存</strong>」ボタンで設定が即座に反映されます。
        次回以降の送信モーダルから新しい設定で動作します。
      </p>
      <p>
        変更を取り消したい場合は「<strong>リセット</strong>」ボタンで保存前の状態に戻せます。
      </p>

      <Callout type="warning" title="保存前の変更は破棄される">
        タブ切り替えやページ遷移しても保存前の変更は破棄されます。
        変更後は必ず「マッピングを保存」を押してください。
      </Callout>
    </>
  );
}
